import {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Controller,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import styled from "styled-components";
import theme from "../styles/theme";
import Button from "./Button";
import FlexBox, { FlexGrow } from "./FlexBox";
import Input, { InputTextArea } from "./Input";
import { StyledText } from "./Text";
import Icon, { ClickableIcon } from "./Icon";
import Status from "../types/enum/Status";
import { CancelButton, CustomFlexFixed } from "./CourseOverviewModify";
import { useScreenClass } from "react-grid-system";
import { useLocation, useNavigate } from "react-router-dom";
import { SaveButton } from "./PillarOverviewModify";
import { LocationInterface } from "./UtilsComponents";
import ConfirmChanges from "./ConfirmChanges";
import { UserContext } from "../context/UserContext";
type PillarSubdomainModifyType = {
  showBackButton?: boolean;
  goBack: () => void;
  onSaveComplete: (data: any) => void;
  subdomainData: {
    id: string;
    description: string;
    title: string;
  } | null;
};

const Header = styled.h4`
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0px;
  text-align: left;
`;
const CustomFlex = styled(FlexBox)`
  padding: 20px;
  align-items: center;
  border-bottom: 1px solid #3c406c;
`;
const CustomFlexPlain = styled(FlexBox)`
  padding: 20px 0;
  align-items: flex-start;
`;
const CustomGrowPlain = styled(FlexGrow)`
  min-width: 150px;
  flex-grow: 0;
`;
const Label = styled.h5`
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
  color: black;
`;
interface SubdomainInputs {
  title: string;
  description: string;
}

function PillarSubdomainModify({
  subdomainData,
  goBack,
  showBackButton = false,
  onSaveComplete,
}: PillarSubdomainModifyType) {
  const screenClass = useScreenClass();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { isDirty, errors },
  } = useForm<SubdomainInputs>({
    mode: "onBlur",
    defaultValues: {
      title: subdomainData ? subdomainData.title : "",
      description: subdomainData ? subdomainData.description : "",
    },
  });
  const { setModified, isOpen, setOpen } = useContext(UserContext);

  useEffect(() => {
    reset();
    mapData(subdomainData);
  }, [subdomainData]);

  useEffect(() => {
    isDirty && setModified(true);
  }, [isDirty]);

  const isMobile = useMemo(
    () => ["xs", "sm"].includes(screenClass),
    [screenClass]
  );
  const onSubmit: SubmitHandler<SubdomainInputs> = (data: any) => {
    setOpen(false);
    setModified(false);
    mapData(subdomainData);
    reset();
    onSaveComplete(data);
  };
  let location = useLocation() as LocationInterface;
  const [count, setCount] = useState({ name: 0, description: 0 });

  function navigateToList() {
    let strLocation = location.pathname.split("/");
    let removeEnd = strLocation.pop();
    let urlJoin = strLocation.join("/");
    console.log(urlJoin,strLocation)
    return navigate(urlJoin);
  }

  const mapData = (data: any) => {
    setValue("title", data?.title || "");
    setValue("description", data?.description || "");
  };

  const discardFunction = () => {
    setOpen(false);
    setModified(false);
    reset();
    // mapData(pillarData);
  };

  return (
    <>
      {isOpen && (
        <ConfirmChanges
          discardFunction={discardFunction}
          isOpen={isOpen}
          setOpen={setOpen}
          confirmFunction={handleSubmit(onSubmit)}
          title={"Do you want to save your changes?"}
          description={
            "If you leave page, any changes you have made will be lost."
          }
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <CustomFlex
          style={{
            borderBottom: "none",
            alignItems: "flex-start",
            overflowY: "scroll",
          }}
        >
          <FlexGrow>
            <CustomFlexPlain>
              <CustomGrowPlain>
                <Label>Title</Label>
              </CustomGrowPlain>
              <FlexGrow>
                <Input
                  maxLength={64}
                  style={{ width: "100%" }}
                  isFullWidth
                  {...register("title", { required: true })}
                />
                {errors.title && (
                  <p
                    style={{
                      marginTop: 0,
                      color: theme.color.red800,
                    }}
                  >
                    This field is required
                  </p>
                )}
              </FlexGrow>
            </CustomFlexPlain>
            <CustomFlexPlain>
              <CustomGrowPlain>
                <Label>Description</Label>
              </CustomGrowPlain>
              <FlexGrow>
                <FlexBox
                  space={0}
                  alignItems="start"
                  style={{ width: "100%" }}
                  direction="column"
                >
                  <FlexGrow style={{ width: "100%" }}>
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <>
                          <InputTextArea
                            isFullWidth
                            style={{ minHeight: "100px", width: "100%" }}
                            value={field.value}
                            maxLength={2048}
                            onChange={(e: any) => {
                              field.onChange(e);
                              setCount({
                                ...count,
                                description: e.target.value.length,
                              });
                            }}
                          />
                          <FlexGrow style={{ textAlign: "end" }}>
                            <StyledText
                              style={{ fontSize: "14px" }}
                              color={theme.color.lightBlueGray500}
                            >
                              {count.description || field.value.length}/2048
                            </StyledText>
                          </FlexGrow>
                        </>
                      )}
                    />
                    {/* <InputTextArea
                      isFullWidth
                      style={{ minHeight: "100px", width: "100%" }}
                      // onChange={e => setCount(e.target.value.length)}
                      {...register("description")}
                    /> */}
                  </FlexGrow>
                </FlexBox>
              </FlexGrow>
            </CustomFlexPlain>
          </FlexGrow>
        </CustomFlex>
        <CustomFlexFixed>
          {showBackButton && (
            <Button
              key={0}
              style={{ marginLeft: "30px" }}
              type="button"
              onClick={() => {
                goBack();
              }}
              status={Status.primary}
              isOutline
            >
              <Icon style={{ paddingRight: "8px" }} name={"arrow_back"} /> Back
            </Button>
          )}
          <FlexGrow
            style={{
              textAlign: "end",
              marginRight: "30px",
              paddingTop: 10,
              paddingBottom: 10,
            }}
          >
            <CancelButton
              className="reduce_opct_onhover"
              key={1}
              type="button"
              onClick={() => {
                navigateToList();
              }}
              status={Status.danger}
              isOutline={false}
            >
              Cancel
            </CancelButton>
            <SaveButton
              className="reduce_opct_onhover"
              key={2}
              type="submit"
              status={Status.primary}
              isOutline={false}
            >
              <Icon style={{ paddingRight: "8px" }} name={"save"} /> Save
            </SaveButton>
          </FlexGrow>
        </CustomFlexFixed>
      </form>
    </>
  );
}
export default PillarSubdomainModify;
